import { ChatBubbleLeftIcon } from '@heroicons/react/20/solid';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import * as React from 'react';

import { TimelineEventRouter } from '@/components/tasks/task-detail/timeline/TimelineEventRouter';
import { TimelineItemComment } from '@/components/tasks/task-detail/timeline/TimelineItemComment';
import { TimelineItemCorrelated } from '@/components/tasks/task-detail/timeline/TimelineItemCorrelated';
import { useTimeline } from '@/components/tasks/task-detail/timeline/useTimeline';
import { cn } from '@/lib/utils';

interface TaskActivityTimelineProps {
  matterId: string;
  taskId: string;
  /** Title for the collapsible section */
  title?: string;
  /** Whether the section starts expanded */
  defaultExpanded?: boolean;
  /** Maximum number of items to show */
  limit?: number;
  /** Custom class for the container */
  className?: string;
}

// No-op handlers for read-only mode
const noopEdit = () => {};
const noopDelete = () => {};
const noopCancel = () => {};
const noopSave = async () => {};

/**
 * Read-only, collapsible task activity timeline.
 * Reuses the existing timeline infrastructure from task detail view.
 */
export const TaskActivityTimeline: React.FC<TaskActivityTimelineProps> = ({
  matterId,
  taskId,
  title = 'Task Updates',
  defaultExpanded = false,
  limit = 15,
  className,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const { items, isLoading, error } = useTimeline({
    matterId,
    taskId,
    filter: 'task',
    sourceFilter: 'all',
  });
  const hasError = Boolean(error);

  const displayItems = limit ? items.slice(0, limit) : items;
  const hasMore = items.length > displayItems.length;

  return (
    <div
      className={cn(
        'border border-zinc-200 rounded-lg bg-white overflow-hidden',
        className,
      )}
    >
      {/* Collapsible Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors"
      >
        <span className="text-sm font-semibold text-zinc-700">{title}</span>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-zinc-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-zinc-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-zinc-100">
          {hasError && (
            <div className="text-xs text-red-600 py-4 px-4">
              Failed to load updates
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
            </div>
          )}

          {!isLoading && !hasError && items.length === 0 && (
            <div className="py-6 flex flex-col items-center justify-center text-zinc-400">
              <div className="h-10 w-10 rounded-full bg-zinc-50 flex items-center justify-center mb-1">
                <ChatBubbleLeftIcon className="h-5 w-5 text-zinc-300" />
              </div>
              <p className="text-xs font-medium">No activity yet</p>
            </div>
          )}

          {!isLoading && !hasError && displayItems.length > 0 && (
            <div className="divide-y divide-zinc-100">
              {displayItems.map((item) => (
                <div key={item.id} className="px-4 py-2">
                  {item.type === 'event' && (
                    <TimelineEventRouter
                      activityItem={item.activityItem}
                      matterId={matterId}
                    />
                  )}
                  {item.type === 'update' && (
                    <TimelineItemComment
                      update={item.update}
                      taskId={taskId}
                      linkedExternalTasks={[]}
                      isEditing={false}
                      onEdit={noopEdit}
                      onDelete={noopDelete}
                      onCancelEdit={noopCancel}
                      onSaveEdit={noopSave}
                    />
                  )}
                  {item.type === 'correlated' && (
                    <TimelineItemCorrelated
                      activityItem={item.activityItem}
                      update={item.update}
                      isEditing={false}
                      onEdit={noopEdit}
                      onCancelEdit={noopCancel}
                      onSaveEdit={noopSave}
                    />
                  )}
                </div>
              ))}
              {hasMore && (
                <div className="px-4 py-2 text-center">
                  <span className="text-xs text-zinc-500">
                    +{items.length - displayItems.length} more items
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskActivityTimeline;
